import { useDataCatalog } from "@/composable/data-catalog";
import { SoundDataType, SoundPonderation, SpectrumType } from "@soft-db/data-formatter";
import { FieldFactory } from "@soft-db/form";
import { Show } from "@soft-db/soft-utils";
import { SoftInput, SoftSelect } from "@soft-db/ui";
import { PropType, computed, defineComponent } from "vue";
import * as z from "zod";
import FrequencyInput from "./FrequencyInput";
import { QueryDataConfigForm } from "./QueryDataConfig";
import SpectrumSelection from "./SpectrumSelection";

export default defineComponent({
	name: "SoundDataConfigSettings",
	components: { SpectrumSelection },
	props: {
		useField: {
			type: Function as PropType<FieldFactory<QueryDataConfigForm>>,
			required: true,
		},
		showSpectrum: Boolean,
		showFrequency: Boolean,
	},
	setup(props) {
		const { t } = useI18n();

		const dataField = props.useField("$dataConfig.data", { schema: z.nativeEnum(SoundDataType) });

		const ponderationField = props.useField("$dataConfig.ponderation", {
			schema: z.nativeEnum(SoundPonderation),
		});

		const statsField = props.useField("$dataConfig.stats", {
			isNumber: true,
			min: 1,
			max: 99,
			isRequired: computed(() => dataField.data.value === SoundDataType.LN),
		});

		const spectrumField = props.useField("$dataConfig.spectrum", {
			schema: () => {
				const schema = z.nativeEnum(SpectrumType);
				if (props.showSpectrum) return schema;

				return schema.optional();
			},
		});

		const { generateMenuEntries, Sound, SoundPonderation: soundPonderationMap } = useDataCatalog();

		const dataTypes = generateMenuEntries(Sound);
		const ponderationType = generateMenuEntries(soundPonderationMap);

		const isStats = computed(() => dataField.data.value === "ln");

		return () => (
			<>
				<SoftSelect
					label={t("common.dataType")}
					items={dataTypes}
					itemText="name"
					itemValue="value"
					{...dataField.attrs}
				/>
				<SoftSelect
					label={t("dashboard.ponderation")}
					items={ponderationType}
					itemText="name"
					itemValue="value"
					{...ponderationField.attrs}
				/>
				<Show when={isStats.value}>
					<SoftInput label={t("dashboard.percentage")} type="number" {...statsField.attrs} />
				</Show>

				<Show when={props.showSpectrum}>
					<SpectrumSelection label={t("dashboard.spectrum")} {...spectrumField.attrs} />
				</Show>
				<Show when={props.showFrequency}>
					<FrequencyInput useField={props.useField} />
				</Show>
			</>
		);
	},
});
